import { useCallback, useEffect, useState } from 'react';
import pushNotificationService from '../services/pushNotificationService';

/**
 * usePushNotifications Hook
 *
 * Drives the browser Push API subscribe/unsubscribe flow and keeps it in
 * sync with the backend (/api/push/...). Degrades gracefully when Push
 * isn't supported (e.g. local `npm start`, where the service worker only
 * registers in production builds — see src/serviceWorkerRegistration.js) —
 * callers should treat `isSupported === false` as "hide the UI", not error.
 *
 * @returns {object}
 *   - isSupported: boolean - Push API + Service Worker available in this browser
 *   - isSubscribed: boolean - THIS browser/device has an active subscription
 *   - loading: boolean - initial subscription-state check in progress
 *   - subscribing: boolean - a subscribe/unsubscribe action is in flight
 *   - subscribe: () => Promise<boolean> - runs the full subscribe flow
 *   - unsubscribe: () => Promise<boolean> - runs the full unsubscribe flow
 *   - refresh: () => Promise<void> - re-checks subscription state
 */
export function usePushNotifications() {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);

  const checkSupport = useCallback(() => {
    return 'serviceWorker' in navigator && 'PushManager' in window;
  }, []);

  const refresh = useCallback(async () => {
    if (!checkSupport()) {
      setIsSupported(false);
      setIsSubscribed(false);
      setLoading(false);
      return;
    }
    setIsSupported(true);
    try {
      // Use getRegistration (not .ready) here — .ready never resolves if no
      // service worker is registered (e.g. local dev), which would hang the
      // initial state check forever.
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        setIsSubscribed(false);
        setLoading(false);
        return;
      }
      const existingSubscription = await registration.pushManager.getSubscription();
      if (!existingSubscription) {
        setIsSubscribed(false);
        setLoading(false);
        return;
      }
      // A subscription exists in the browser — confirm it's actually one
      // the backend still knows about, by matching endpoints rather than
      // just checking "any subscription exists for this account".
      const mine = await pushNotificationService.getMySubscriptions();
      const matches = (mine || []).some((s) => s.endpoint === existingSubscription.endpoint);
      setIsSubscribed(matches);
    } catch (error) {
      console.error('Error checking push subscription state:', error);
      setIsSubscribed(false);
    } finally {
      setLoading(false);
    }
  }, [checkSupport]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const subscribe = useCallback(async () => {
    if (!checkSupport()) {
      throw new Error('Push notifications are not supported in this browser.');
    }
    setSubscribing(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        throw new Error('Notification permission was not granted.');
      }

      // navigator.serviceWorker.ready never resolves without an active
      // service worker (e.g. local dev builds, which skip SW registration
      // entirely). Guard with getRegistration() first so we fail fast with
      // a clear message instead of hanging.
      const existingRegistration = await navigator.serviceWorker.getRegistration();
      if (!existingRegistration) {
        throw new Error('Push notifications require the installed app (no active service worker was found).');
      }
      const registration = await navigator.serviceWorker.ready;

      const { public_key: publicKey } = await pushNotificationService.getVapidPublicKey();
      if (!publicKey) {
        throw new Error('Push notifications are not available right now.');
      }

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });

      const { endpoint, keys } = subscription.toJSON();
      await pushNotificationService.subscribe({
        endpoint,
        p256dh_key: keys?.p256dh,
        auth_key: keys?.auth,
        user_agent: navigator.userAgent,
      });

      setIsSubscribed(true);
      return true;
    } finally {
      setSubscribing(false);
    }
  }, [checkSupport]);

  const unsubscribe = useCallback(async () => {
    if (!checkSupport()) return false;
    setSubscribing(true);
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        setIsSubscribed(false);
        return true;
      }
      const subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        setIsSubscribed(false);
        return true;
      }
      const endpoint = subscription.endpoint;
      await subscription.unsubscribe();
      await pushNotificationService.unsubscribe(endpoint);
      setIsSubscribed(false);
      return true;
    } finally {
      setSubscribing(false);
    }
  }, [checkSupport]);

  return { isSupported, isSubscribed, loading, subscribing, subscribe, unsubscribe, refresh };
}

/**
 * Converts a base64url-encoded VAPID public key into the Uint8Array format
 * required by PushManager.subscribe's applicationServerKey option.
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default usePushNotifications;
